import { useState } from 'react';
import { motion } from 'framer-motion';
import InputField from './ui/InputField';
import { COLORS, EASING } from '../constants/theme';

const CONTACT_URL = 'https://bixtq589f8.execute-api.us-east-1.amazonaws.com/prod/contact';

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus]         = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name:    String(data.get('fullName') || '').trim(),
      email:   String(data.get('email')    || '').trim(),
      phone:   String(data.get('phone')    || '').trim(),
      message: String(data.get('message')  || '').trim(),
      source: 'website-contact-form',
    };
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus({ type: 'success', message: 'Thanks — we received your message and will be in touch shortly.' });
      form.reset();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" style={{ padding: '96px 0', background: COLORS.bg }}>
      <div className="container">
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1.5fr',
          borderRadius: 20, overflow: 'hidden',
          boxShadow: '0 8px 48px rgba(15,35,53,0.1)',
        }} className="contact-grid">

          {/* Left teal panel */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: EASING.spring }}
            style={{
              background: `linear-gradient(160deg, ${COLORS.blue} 0%, ${COLORS.blueDark} 100%)`,
              padding: '52px 40px',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>
                Contact Reano
              </div>
              <h2 style={{ fontSize: 'clamp(24px,2.5vw,34px)', fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.18, color: COLORS.white, marginBottom: 20 }}>
                Let's architect your next AI initiative.
              </h2>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.68, marginBottom: 44 }}>
                Ready to transform your workflows with AI? Our team is here to help you discover, build, and deploy.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {[
                  {
                    icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 2C6.2 2 4 4.2 4 7c0 4 5 9 5 9s5-5 5-9c0-2.8-2.2-5-5-5z"/><circle cx="9" cy="7" r="1.8"/></svg>,
                    label: 'Location', value: 'Gurugram, India',
                  },
                  {
                    icon: <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="4" width="14" height="11" rx="2"/><path d="M2 6l7 5 7-5"/></svg>,
                    label: 'Email', value: 'support@reanoai.com',
                  },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                      color: COLORS.white, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{item.icon}</div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: 'rgba(255,255,255,0.6)', marginBottom: 3 }}>{item.label}</div>
                      <div style={{ fontSize: 14.5, color: COLORS.white, fontWeight: 500 }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 48, display: 'flex', gap: 8 }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.3)', flex: n === 1 ? 2 : 1 }}/>
              ))}
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: EASING.spring, delay: 0.08 }}
            style={{ background: COLORS.white, padding: '52px 44px' }}
          >
            <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.025em', color: COLORS.text, marginBottom: 8 }}>
              Tell Us About Your AI Goal
            </h3>
            <p style={{ fontSize: 14, color: COLORS.text3, marginBottom: 32 }}>We typically respond within one business day.</p>

            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <InputField label="Full Name *" type="text"  name="fullName" required placeholder="Your name" />
                <InputField label="Phone *"     type="tel"   name="phone"    required placeholder="+1-888-888-8888" />
              </div>
              <InputField label="Email *" type="email" name="email" required placeholder="you@company.com" />
              <InputField
                label="Message *"
                as="textarea"
                name="message"
                required
                rows={4}
                placeholder="Describe your AI goals or challenge..."
                style={{ resize: 'vertical', minHeight: 108 }}
              />

              {status && (
                <div style={{
                  padding: '11px 15px', borderRadius: 9, fontSize: 13.5,
                  background: status.type === 'success' ? 'rgba(71,158,195,0.08)' : 'rgba(220,38,38,0.07)',
                  border: `1px solid ${status.type === 'success' ? 'rgba(71,158,195,0.3)' : 'rgba(220,38,38,0.25)'}`,
                  color: status.type === 'success' ? COLORS.blue : '#dc2626',
                }}>{status.message}</div>
              )}

              <button type="submit" className="btn-primary" disabled={submitting} style={{
                justifyContent: 'center', fontSize: 15, padding: '14px 0',
                opacity: submitting ? 0.72 : 1, cursor: submitting ? 'not-allowed' : 'pointer',
              }}>
                {submitting ? 'Sending…' : 'Send Message'}
                {!submitting && (
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 7.5h11M8.5 3l4 4.5-4 4.5"/>
                  </svg>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
